import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState } from 'react'
import { MenuItem, TextField } from '@mui/material'

import { peruBanksData } from './lib'
import { BankAccount, Customer, FormError } from '../../types'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { handleGetParsedValue, numberWithCommas } from '../../utils'
import { peruvianBankAccountType, peruvianCustomersDocumentType } from '../../lib'
import { AppStateContext, UserContext } from '../../contexts'
import { Pocket } from '../../utils/dexie/db'

export const PeruvianCustomerPaymentForm = ({
  errors,
  customerData,
  amountToSend,
  selectedPocketId,
  selectedCurrency,
  handleSetPocketId,
  handleSetCustomerData,
  handleSetPaymentAmountToSend
}: {
  amountToSend: string
  selectedCurrency: string
  selectedPocketId?: string
  errors?: { [key: string]: string } | FormError
  customerData: Partial<Customer> | null
  handleSetPocketId?: (pocketIdToSet: string) => void
  handleSetPaymentAmountToSend: (paymentAmountToSend: string) => void
  handleSetCustomerData: (attributeToSet: string, valueToSet: string | Array<BankAccount>) => void
}) => {
  const location = useLocation()
  const { userSession } = useContext(UserContext)
  const { t } = useTranslation(['individual-money-transfers-management'])
  const [banksData, setBanksData] = useState<Array<{ name: string; code: string }>>([])
  const { currentPocketsData, defaultPocketIdSelected } = useContext(AppStateContext)

  useEffect(() => {
    if (userSession?.role === 'super-admin-client' && currentPocketsData?.length === 1 && defaultPocketIdSelected) {
      handleSetPocketId?.(defaultPocketIdSelected)
    }

    if ((userSession?.role === 'admin-client' || userSession?.role === 'admin-client-builder') && defaultPocketIdSelected) {
      handleSetPocketId?.(defaultPocketIdSelected)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, currentPocketsData, defaultPocketIdSelected])

  useEffect(() => {
    const banksDataSorted = peruBanksData.sort((a, b) => a.name.localeCompare(b.name))
    setBanksData(banksDataSorted)
  }, [])

  const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = e.target.value

    if (input === '') {
      return ''
    }

    const first = input[0]

    if (/[0-9]/.test(first)) {
      if (/^\d+$/.test(input)) {
        return input
      }
    }

    if (first === '@') {
      const pattern = /^@[A-Za-z0-9]*$/
      if (pattern.test(input)) {
        return input
      }
    }

    if (/[A-Za-z]/.test(first)) {
      const allowedChars = /^[A-Za-z0-9._%+\-@]*$/
      if (!allowedChars.test(input)) {
        return
      }

      const atCount = (input.match(/@/g) || []).length
      if (atCount > 1) {
        return
      }

      return input
    }
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px', padding: '20px 0px', boxSizing: 'border-box', borderTop: '1px solid #DDD' }}>
      <div className="customers-add-new-form-personal-data-title">{t('transactionModal.title', { ns: 'individual-money-transfers-management' })}</div>
      <div className="customers-add-new-form-personal-data-container">
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label={t('transactionModal.fieldLabels.firstName', { ns: 'individual-money-transfers-management' })}
              error={!!errors?.firstName}
              helperText={errors?.firstName}
              value={customerData?.firstName || ''}
              onChange={(event) => {
                handleSetCustomerData('firstName', event.target.value.toUpperCase())
              }}
            />
          </div>
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label={t('transactionModal.fieldLabels.lastName', { ns: 'individual-money-transfers-management' })}
              error={!!errors?.lastName}
              helperText={errors?.lastName}
              value={customerData?.lastName || ''}
              onChange={(event) => {
                handleSetCustomerData('lastName', event.target.value.toUpperCase())
              }}
            />
          </div>
        </div>
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label={t('transactionModal.fieldLabels.documentType', { ns: 'individual-money-transfers-management' })}
              error={!!errors?.documentType}
              helperText={errors?.documentType}
              value={customerData?.documentType || ''}
              onChange={(event) => {
                handleSetCustomerData('documentType', event.target.value)
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('paymentForm.select', { ns: 'individual-money-transfers-management' })}
              </MenuItem>
              {Object.keys(peruvianCustomersDocumentType).map((documentType: string) => {
                return (
                  <MenuItem
                    key={documentType}
                    value={documentType}
                  >
                    {(peruvianCustomersDocumentType as any)[documentType].toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label={t('transactionModal.fieldLabels.documentNumber', { ns: 'individual-money-transfers-management' })}
              error={!!errors?.documentNumber}
              helperText={errors?.documentNumber}
              value={customerData?.documentNumber || ''}
              onChange={(event) => {
                handleSetCustomerData('documentNumber', event.target.value.toUpperCase())
              }}
            />
          </div>
        </div>
      </div>
      <div className="customers-add-new-form-bank-data-container">
        <div className="customers-add-new-form-bank-data-title">{t('transactionModal.bankDataTitle', { ns: 'individual-money-transfers-management' })}</div>
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label={t('transactionModal.fieldLabels.bankAccountType', { ns: 'individual-money-transfers-management' })}
              error={!!errors?.bankAccountType}
              helperText={errors?.bankAccountType}
              value={Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0 ? customerData?.bankAccountsData[0].bankAccountType : ''}
              onChange={(event) => {
                const currentCustomerBankAccountsData: Array<BankAccount> =
                  Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0
                    ? [...customerData?.bankAccountsData]
                    : [{ bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: true }]
                currentCustomerBankAccountsData[0].bankAccountType = event.target.value

                handleSetCustomerData('bankAccountsData', [currentCustomerBankAccountsData[0]])
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('paymentForm.select', { ns: 'individual-money-transfers-management' })}
              </MenuItem>
              {Object.keys(peruvianBankAccountType as { [key: string]: string }).map((bankAccountType: string) => {
                return (
                  <MenuItem
                    key={bankAccountType}
                    value={bankAccountType}
                  >
                    {(peruvianBankAccountType as any)[bankAccountType].toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>

          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label={t('transactionModal.fieldLabels.bankName', { ns: 'individual-money-transfers-management' })}
              error={!!errors?.bankName}
              helperText={errors?.bankName}
              value={
                customerData?.bankAccountsData &&
                Array.isArray(customerData?.bankAccountsData) &&
                customerData?.bankAccountsData.length > 0 &&
                customerData?.bankAccountsData[0].bankName &&
                customerData?.bankAccountsData[0].bankAchCode
                  ? `${customerData?.bankAccountsData[0].bankName}-${customerData?.bankAccountsData[0].bankAchCode}`
                  : ''
              }
              onChange={(event) => {
                const currentCustomerBankAccountsData: Array<BankAccount> =
                  Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0
                    ? [...customerData?.bankAccountsData]
                    : [{ bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: true }]
                currentCustomerBankAccountsData[0].bankName = event.target.value.split('-')[0]
                currentCustomerBankAccountsData[0].bankAchCode = event.target.value.split('-')[1]

                handleSetCustomerData('bankAccountsData', [currentCustomerBankAccountsData[0]])
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('paymentForm.select', { ns: 'individual-money-transfers-management' })}
              </MenuItem>
              {banksData?.map((bankData: { name: string; code: string }) => {
                return (
                  <MenuItem
                    key={bankData.code}
                    value={`${bankData.name}-${bankData.code}`}
                  >
                    {bankData.name.toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
        </div>
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label={t('transactionModal.fieldLabels.bankAccountNumber', { ns: 'individual-money-transfers-management' })}
              error={!!errors?.bankAccountNumber}
              helperText={errors?.bankAccountNumber}
              value={Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0 ? customerData?.bankAccountsData[0].bankNumber : ''}
              onChange={(event) => {
                // const validPattern = /^(@[A-Za-z0-9]*|\d*)$/

                // if (!validPattern.test(event.target.value)) return
                const valueToSet = handleAccountNumberChange(event)

                const currentCustomerBankAccountsData: Array<BankAccount> =
                  Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0
                    ? [...customerData?.bankAccountsData]
                    : [{ bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: true }]
                // currentCustomerBankAccountsData[0].bankNumber = event.target.value
                currentCustomerBankAccountsData[0].bankNumber = valueToSet ? String(valueToSet) : ''

                handleSetCustomerData('bankAccountsData', [currentCustomerBankAccountsData[0]])
              }}
            />
          </div>
        </div>
        {handleSetPaymentAmountToSend && (
          <div className="customers-add-new-form-personal-data-form-row">
            <div className="customers-add-new-form-personal-data-form-field-container">
              <TextField
                fullWidth
                variant="standard"
                sx={textFieldWhiteBg}
                value={amountToSend}
                error={!!errors?.amountToSend}
                helperText={errors?.amountToSend}
                label={t('transactionModal.fieldLabels.amountToSend', { ns: 'individual-money-transfers-management', selectedCurrency })}
                onBlur={(event) => {
                  if (event.target.value === '') {
                    handleSetPaymentAmountToSend(`$ ${numberWithCommas(Number(amountToSend))}`)
                  }
                }}
                onChange={(event) => {
                  const extractedNumber = event.target.value[0] === '$' ? Number(event.target.value.slice(1).trim().replaceAll(',', '')) : Number(event.target.value.trim().replaceAll(',', ''))

                  if (isNaN(extractedNumber)) {
                    return
                  }

                  if (event.target.value.trim() === '$' || event.target.value.trim() === '0') {
                    return handleSetPaymentAmountToSend('')
                  }

                  const valueToSet = handleGetParsedValue(event)
                  handleSetPaymentAmountToSend(valueToSet)
                }}
              />
            </div>
          </div>
        )}
        {userSession?.role === 'super-admin-client' && Number(currentPocketsData?.length) > 0 && (location.pathname === '/' || location.pathname === '/beneficiaries') && (
          <div className="customers-add-new-form-personal-data-form-row">
            <TextField
              select
              fullWidth
              variant="standard"
              value={selectedPocketId || ''}
              label={t('paymentForm.fundsFrom', { ns: 'individual-money-transfers-management' })}
              error={!!errors?.selectedPocketId}
              helperText={errors?.selectedPocketId}
              onChange={(event) => handleSetPocketId?.(event.target.value)}
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('paymentForm.select', { ns: 'individual-money-transfers-management' })}
              </MenuItem>
              {currentPocketsData?.map((pocketData: Pocket) => {
                return (
                  <MenuItem
                    key={String(pocketData.id)}
                    value={String(pocketData.id)}
                  >
                    {`${pocketData.name?.toUpperCase()} - $ ${numberWithCommas(Number(pocketData.balance))}`}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
        )}
      </div>
    </div>
  )
}
